/* ==========================================================================
 * MIXED BY NASRIN — quick add on product cards (assets/mixed-quick-add.js)
 * --------------------------------------------------------------------------
 * Markup: snippets/mixed-quick-add.liquid (rendered inside each card by
 * snippets/card-product.liquid); styles: assets/component-mixed-quick-add.css.
 * Loaded, deferred, by the sections that render product cards while
 * settings.quick_add_enabled is on. Runs after global.js (fetchConfig,
 * trapFocus) and the cart drawer or notification element, which it reuses.
 *
 * Two ways in, one add:
 *   <mixed-quick-add>  the desktop bar on the photo; a click on the bar opens
 *                      the sizes above it (the stylesheet only colours it on
 *                      hover), a click on a size adds. The sizes close when
 *                      the pointer leaves the card, on a press elsewhere, on
 *                      Escape, or when focus leaves. One element per card, so
 *                      the grid can be swapped by the filters (facets.js) and
 *                      the new cards upgrade on their own.
 *   [data-mqa-open]    the + in the title row on phones, tablets and any
 *                      screen without a mouse. Opens ONE sheet, built here on
 *                      first use and kept in <body>, filled from that card's
 *                      <mixed-quick-add>. A one-size product adds straight
 *                      away, no sheet.
 *   A sold-out product carries only a disabled bar (data-sold-out, reading
 *   sold out) and no +: nothing to bind, nothing to add (Joseph, 2026-10-20).
 *
 * The add itself mirrors assets/product-form.js: a form post to
 * routes.cart_add_url with the drawer's section ids, then
 * cart.renderContents() re-renders and opens the drawer (or the
 * notification). Pre-order sizes carry the same _is_preorder /
 * _expected_date line properties as the product page, read from the per-size
 * map snippets/product-preorder.liquid prints into the card (part 'map').
 * Nothing here decides what a pre-order is.
 *
 * Size memory: the size chosen here is saved under the same localStorage key
 * snippets/size-memory.liquid uses, and a saved size is pre-selected in the
 * sheet, so a shopper who picks M once keeps M across the grid and the PDP.
 *
 * Photos (Joseph, 2026-10-19): the sheet opens with a swipeable strip of the
 * product's photos above the name, like the PDP's mobile gallery, sized by
 * the viewport so the sheet still fits a phone. The first slide is the card's
 * own photo, there at once; the rest come from /products/<handle>.js (the
 * media list, images only, in PDP order), fetched once per product and kept.
 * ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('mixed-quick-add')) return;

  var SIZE_KEY = 'mbn_pref_size'; // shared with snippets/size-memory.liquid
  var ADDED_TICK_MS = 2000;
  var ERROR_HOLD_MS = 3500;
  var MEDIA_WIDTH = 480;          // CDN width of the sheet's photos (2x of their ~220px box)
  var mediaCache = {};            // handle -> [{ src, alt, ratio }], or a pending Promise

  /** The product's photos in PDP order, from /products/<handle>.js, once per product. */
  function productMedia(handle) {
    if (!handle) return Promise.resolve([]);
    if (mediaCache[handle]) return Promise.resolve(mediaCache[handle]);
    var request = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : Promise.reject(new Error(String(response.status))); })
      .then(function (product) {
        var list = (product.media || []).filter(function (m) { return m.media_type === 'image' && m.src; }).map(function (m) {
          return { src: m.src + (m.src.indexOf('?') === -1 ? '?' : '&') + 'width=' + MEDIA_WIDTH, alt: m.alt || product.title || '', ratio: m.aspect_ratio || 0.8 };
        });
        mediaCache[handle] = list;
        return list;
      })
      .catch(function (error) { delete mediaCache[handle]; console.error(error); return []; });
    mediaCache[handle] = request;
    return request;
  }

  function strings() {
    return window.variantStrings || {};
  }

  function readSavedSize() {
    try { return localStorage.getItem(SIZE_KEY); } catch (e) { return null; }
  }

  function writeSavedSize(value) {
    if (!value) return;
    try { localStorage.setItem(SIZE_KEY, value); } catch (e) { /* private mode: remember nothing */ }
  }

  function cartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function errorText(error) {
    if (error && error.cartError && error.message) return error.message;
    return (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
  }

  /* ---- pre-order: the per-size map the card carries ----------------------- */

  function preorderMap(source) {
    if (source.mqaPreorderMap) return source.mqaPreorderMap;
    var map = {};
    var node = source.querySelector('[data-mqa-preorder]');
    if (node) {
      try { map = JSON.parse(node.textContent) || {}; } catch (e) { map = {}; }
    }
    source.mqaPreorderMap = map;
    return map;
  }

  /** The map entry when this size is a pre-order, else null. */
  function preorderFor(source, variantId) {
    var entry = preorderMap(source)[String(variantId)];
    return entry && entry.state === 'preorder' ? entry : null;
  }

  function buttonLabel(source, variantId) {
    var s = strings();
    if (preorderFor(source, variantId)) return s.preorder || 'pre-order now';
    return s.addToCart || 'add to bag';
  }

  /* ---- the add ------------------------------------------------------------ */

  /**
   * Adds one of the variant the way product-form.js does, so the drawer (or
   * notification) re-renders from the same response and opens. Resolves with
   * the cart response; rejects with an Error whose message is Shopify's own
   * description when the add was refused (sold out meanwhile, limits).
   */
  function addToCart(source, variantId, opener) {
    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');

    var pre = preorderFor(source, variantId);
    if (pre) {
      body.append('properties[_is_preorder]', pre.text || '');
      if (pre.date) body.append('properties[_expected_date]', pre.date);
    }

    var cart = cartElement();
    if (cart) {
      body.append('sections', cart.getSectionsToRender().map(function (section) { return section.id; }).join(','));
      body.append('sections_url', window.location.pathname);
      cart.setActiveElement(opener || document.activeElement);
    }

    var config = (typeof fetchConfig === 'function')
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { Accept: 'application/javascript' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = body;

    return fetch(window.routes.cart_add_url, config)
      .then(function (response) { return response.json(); })
      .then(function (json) {
        if (json.status) {
          var error = new Error(json.description || json.message || '');
          error.cartError = true;
          throw error;
        }
        if (!cart) {
          window.location = window.routes.cart_url;
          return json;
        }
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(json);
        return json;
      });
  }

  /* ---- desktop: the bar and the sizes above it ---------------------------- */

  class MixedQuickAdd extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      if (this.dataset.soldOut === 'true') return; // a disabled bar reading sold out: nothing to do

      this.bar = this.querySelector('[data-mqa-bar]');
      this.barLabel = this.querySelector('[data-mqa-bar-label]');
      this.single = this.dataset.single === 'true';
      this.busy = false;

      this.annotate();

      if (this.bar) this.bar.addEventListener('click', this.onBarClick.bind(this));
      this.addEventListener('click', this.onSizeClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      // Keyboard: leaving the element closes a panel Enter opened
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setOpen(false);
      });
      // Mouse: the sizes go with the bar when the pointer leaves the card
      var card = this.closest('.card-wrapper');
      if (card) card.addEventListener('pointerleave', () => this.setOpen(false));
    }

    /** Captions the pre-order sizes from the map (the markup says Ready to Ship for every size in stock); the one-size bar says pre-order when its size is one. */
    annotate() {
      this.querySelectorAll('[data-mqa-variant]').forEach((button) => {
        if (!preorderFor(this, button.dataset.mqaVariant)) return;
        button.dataset.preorder = 'true';
        var note = button.querySelector('[data-mqa-note]');
        if (note) note.textContent = 'pre-order';
      });
      if (this.single && this.barLabel && preorderFor(this, this.dataset.variantId)) {
        this.barLabel.textContent = strings().preorder || 'pre-order now';
      }
    }

    setOpen(open) {
      this.classList.toggle('is-open', open);
      if (this.bar && !this.single) this.bar.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    onBarClick() {
      if (this.single) {
        this.add(this.dataset.variantId, this.bar, null);
        return;
      }
      var open = !this.classList.contains('is-open');
      this.setOpen(open);
      if (open) {
        var first = this.querySelector('[data-mqa-variant]:not([disabled])');
        if (first) first.focus();
      }
    }

    onSizeClick(event) {
      var button = event.target.closest('[data-mqa-variant]');
      if (!button || button.disabled || !this.contains(button)) return;
      this.add(button.dataset.mqaVariant, button, button.dataset.option);
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.classList.contains('is-open')) return;
      this.setOpen(false);
      if (this.bar) this.bar.focus();
    }

    add(variantId, opener, option) {
      if (this.busy) return;
      this.busy = true;
      this.classList.add('is-busy');

      var label = this.barLabel;
      var original = label ? label.textContent : '';
      if (label) label.textContent = 'adding';
      if (option && this.dataset.sizeKey === 'size') writeSavedSize(option);

      addToCart(this, variantId, opener)
        .then(() => {
          if (label) label.textContent = original;
          this.setOpen(false);
        })
        .catch((error) => {
          console.error(error);
          if (!label) return;
          label.textContent = errorText(error);
          setTimeout(() => { label.textContent = original; }, ERROR_HOLD_MS);
        })
        .then(() => {
          this.busy = false;
          this.classList.remove('is-busy');
        });
    }
  }

  customElements.define('mixed-quick-add', MixedQuickAdd);

  // A press anywhere else closes an open size list (Safari gives buttons no
  // focus on click, so focusout alone would not)
  document.addEventListener('pointerdown', function (event) {
    document.querySelectorAll('mixed-quick-add.is-open').forEach(function (el) {
      if (!el.contains(event.target)) el.setOpen(false);
    });
  });

  /* ---- phones and touch: the sheet ---------------------------------------- */

  var Sheet = {
    el: null,
    source: null,   // the card's <mixed-quick-add>
    opener: null,   // the + that opened it; focus returns there
    selected: null, // the chosen pill
    busy: false,

    build: function () {
      if (this.el) return;
      var el = document.createElement('div');
      el.className = 'mqa-sheet';
      el.setAttribute('data-mqa-sheet', '');
      el.innerHTML =
        '<div class="mqa-sheet__overlay" data-mqa-close></div>' +
        '<div class="mqa-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="MqaSheetTitle" tabindex="-1">' +
          '<div class="mqa-sheet__media" data-mqa-sheet-media hidden>' +
            '<div class="mqa-sheet__slides" data-mqa-sheet-slides role="region" aria-label="Product photos"></div>' +
            '<div class="mqa-sheet__dots" data-mqa-sheet-dots aria-hidden="true"></div>' +
          '</div>' +
          '<div class="mqa-sheet__header">' +
            '<div class="mqa-sheet__heading">' +
              '<a class="mqa-sheet__title" id="MqaSheetTitle" href="#"></a>' +
              '<div class="mqa-sheet__price"><s data-mqa-sheet-compare hidden></s><span data-mqa-sheet-price></span></div>' +
            '</div>' +
            '<button type="button" class="mqa-sheet__close" data-mqa-close aria-label="Close">' +
              '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="none"><path d="M2 2l12 12M14 2L2 14" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/></svg>' +
            '</button>' +
          '</div>' +
          '<p class="mqa-sheet__label" data-mqa-sheet-label>Size</p>' +
          '<div class="mqa-sheet__sizes" data-mqa-sheet-sizes role="group" aria-label="Size"></div>' +
          '<button type="button" class="button button--primary button--full-width mqa-sheet__submit" data-mqa-sheet-submit disabled>select a size</button>' +
          '<div class="mqa-sheet__ships-in" data-mqa-sheet-ships-in role="status" hidden></div>' +
          '<p class="mqa-sheet__notice" data-mqa-sheet-notice role="alert" hidden></p>' +
        '</div>';
      document.body.appendChild(el);

      this.el = el;
      this.panel = el.querySelector('.mqa-sheet__panel');
      this.media = el.querySelector('[data-mqa-sheet-media]');
      this.slides = el.querySelector('[data-mqa-sheet-slides]');
      this.dots = el.querySelector('[data-mqa-sheet-dots]');
      this.slides.addEventListener('scroll', () => {
        if (this.dotFrame) return;
        this.dotFrame = requestAnimationFrame(() => { this.dotFrame = 0; this.syncDots(); });
      }, { passive: true });
      this.title = el.querySelector('.mqa-sheet__title');
      this.price = el.querySelector('[data-mqa-sheet-price]');
      this.compare = el.querySelector('[data-mqa-sheet-compare]');
      this.label = el.querySelector('[data-mqa-sheet-label]');
      this.sizes = el.querySelector('[data-mqa-sheet-sizes]');
      this.submit = el.querySelector('[data-mqa-sheet-submit]');
      this.shipsIn = el.querySelector('[data-mqa-sheet-ships-in]');
      this.notice = el.querySelector('[data-mqa-sheet-notice]');

      el.querySelectorAll('[data-mqa-close]').forEach((node) => node.addEventListener('click', () => this.close()));
      this.sizes.addEventListener('click', (event) => {
        var pill = event.target.closest('.mqa-sheet__pill');
        if (pill && !pill.disabled && !this.busy) this.select(pill);
      });
      this.submit.addEventListener('click', () => this.add());
      el.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      // Any press outside the panel closes it: pointerdown rather than a
      // click on the overlay, which iOS Safari does not always deliver.
      document.addEventListener('pointerdown', (event) => {
        if (!this.isOpen() || this.panel.contains(event.target)) return;
        this.close();
      });
    },

    isOpen: function () {
      return !!this.el && this.el.classList.contains('is-open');
    },

    /** One slide per photo; the first is the card's own photo so something shows at once. */
    renderMedia: function (list) {
      var ratio = Number(this.source.dataset.ratio) || (list[0] && list[0].ratio) || 0.8;
      this.media.style.setProperty('--mqa-media-ratio', String(ratio));
      this.slides.innerHTML = '';
      list.forEach((item, index) => {
        var slide = document.createElement('div');
        slide.className = 'mqa-sheet__slide';
        var img = document.createElement('img');
        img.src = item.src;
        img.alt = item.alt || '';
        img.loading = index < 2 ? 'eager' : 'lazy';
        img.decoding = 'async';
        slide.appendChild(img);
        this.slides.appendChild(slide);
      });
      this.dots.innerHTML = '';
      list.forEach(() => { var dot = document.createElement('span'); dot.className = 'mqa-sheet__dot'; this.dots.appendChild(dot); });
      this.dots.hidden = list.length < 2;
      this.media.hidden = list.length === 0;
      this.slides.scrollLeft = 0;
      this.syncDots();
    },

    syncDots: function () {
      var slides = this.slides.children;
      if (!slides.length) return;
      var stride = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : 1;
      var index = Math.max(0, Math.min(slides.length - 1, Math.round(this.slides.scrollLeft / stride)));
      Array.prototype.forEach.call(this.dots.children, (dot, i) => dot.classList.toggle('is-active', i === index));
    },

    open: function (source, opener) {
      this.build();
      this.source = source;
      this.opener = opener;
      this.selected = null;
      this.busy = false;

      // Photos: the card's own first, the rest when they arrive (unless another product opened meanwhile)
      var first = source.dataset.image ? [{ src: source.dataset.image, alt: source.dataset.imageAlt || '', ratio: Number(source.dataset.ratio) || 0.8 }] : [];
      this.renderMedia(first);
      productMedia(source.dataset.handle).then((list) => {
        if (this.source !== source || !list.length) return;
        // The same photo under two URLs (the shop's CDN path on the card, cdn.shopify.com in the
        // JSON): keep the card's for that slide so the browser keeps the image it already has.
        var fileOf = function (url) { return String(url).split('?')[0].split('/').pop(); };
        if (first.length) {
          list = list.map(function (item) { return fileOf(item.src) === fileOf(first[0].src) ? { src: first[0].src, alt: item.alt, ratio: item.ratio } : item; });
        }
        this.renderMedia(list);
      });

      this.title.textContent = source.dataset.productTitle || '';
      this.title.href = source.dataset.productUrl || '#';
      this.price.textContent = source.dataset.price || '';
      var compare = source.dataset.comparePrice || '';
      this.compare.textContent = compare;
      this.compare.hidden = !compare;
      this.notice.hidden = true;

      // One pill per size, from the card's own list, so the two never differ
      this.sizes.innerHTML = '';
      var single = source.dataset.single === 'true';
      var saved = source.dataset.sizeKey === 'size' ? readSavedSize() : null;
      var remembered = null;
      source.querySelectorAll('[data-mqa-variant]').forEach((button) => {
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'mqa-sheet__pill';
        pill.dataset.variantId = button.dataset.mqaVariant;
        pill.dataset.option = button.dataset.option || '';
        var text = button.querySelector('.mqa__size-label');
        pill.textContent = text ? text.textContent.trim() : '';
        pill.setAttribute('aria-pressed', 'false');
        if (button.disabled) {
          pill.disabled = true;
          pill.setAttribute('aria-label', pill.textContent + ', ' + (strings().soldOut || 'sold out'));
        } else if (saved && pill.dataset.option === saved) {
          remembered = pill;
        }
        this.sizes.appendChild(pill);
      });

      this.label.hidden = single;
      this.sizes.hidden = single;
      if (single) {
        // Here only to retry a one-size add that failed from the +
        this.selected = { dataset: { variantId: source.dataset.variantId, option: '' } };
      } else if (remembered) {
        this.select(remembered);
      }
      this.syncSubmit();

      this.el.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel);
      else this.panel.focus();
    },

    close: function () {
      if (!this.isOpen()) return;
      this.el.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
      else if (this.opener) this.opener.focus();
    },

    select: function (pill) {
      this.sizes.querySelectorAll('.mqa-sheet__pill').forEach((other) => {
        other.classList.remove('is-selected');
        other.setAttribute('aria-pressed', 'false');
      });
      pill.classList.add('is-selected');
      pill.setAttribute('aria-pressed', 'true');
      this.selected = pill;
      this.notice.hidden = true;
      this.syncSubmit();
    },

    /** The button and the ships-in bar follow the chosen size, as on the PDP. */
    syncSubmit: function () {
      var id = this.selected && this.selected.dataset.variantId;
      if (!id) {
        this.submit.disabled = true;
        this.submit.textContent = 'select a size';
        this.shipsIn.hidden = true;
        return;
      }
      this.submit.disabled = this.busy;
      this.submit.textContent = this.busy ? 'adding' : buttonLabel(this.source, id);
      var pre = preorderFor(this.source, id);
      this.shipsIn.textContent = pre ? (pre.text || '') : '';
      this.shipsIn.hidden = !pre || !pre.text;
    },

    add: function () {
      var id = this.selected && this.selected.dataset.variantId;
      if (!id || this.busy) return;
      this.busy = true;
      this.notice.hidden = true;
      this.syncSubmit();
      if (this.selected.dataset.option && this.source.dataset.sizeKey === 'size') writeSavedSize(this.selected.dataset.option);

      addToCart(this.source, id, this.opener)
        .then(() => {
          this.busy = false;
          this.close();
        })
        .catch((error) => {
          console.error(error);
          this.busy = false;
          this.syncSubmit();
          this.notice.textContent = errorText(error);
          this.notice.hidden = false;
        });
    }
  };

  /* The + in the title row */
  document.addEventListener('click', function (event) {
    var plus = event.target.closest('[data-mqa-open]');
    if (!plus) return;
    event.preventDefault();

    var wrapper = plus.closest('.card-wrapper');
    var source = wrapper && wrapper.querySelector('mixed-quick-add');
    if (!source) return;

    if (source.dataset.single !== 'true') {
      Sheet.open(source, plus);
      return;
    }

    // One size: straight into the bag, a tick on the + while the drawer opens
    if (plus.classList.contains('is-busy')) return;
    plus.classList.add('is-busy');
    addToCart(source, source.dataset.variantId, plus)
      .then(function () {
        plus.classList.add('is-added');
        setTimeout(function () { plus.classList.remove('is-added'); }, ADDED_TICK_MS);
      })
      .catch(function (error) {
        console.error(error);
        // Say so where the shopper can retry: the sheet, with the message
        Sheet.open(source, plus);
        Sheet.notice.textContent = errorText(error);
        Sheet.notice.hidden = false;
      })
      .then(function () {
        plus.classList.remove('is-busy');
      });
  });
})();
